import CheckoutApi from '@support/checkoutApi'

class ApplePayApi {
    constructor() {
    }

    setInstance(instance) {
        this.instance = instance;
        this.$store = instance.$store;
    }

    checkout() {
        return this.$store.getters['checkout/checkout'];
    }

    setRequestStatus(status) {
        this.$store.commit('checkout/setRequestProcessing', status)
    }

    total() {
        return this.$store.getters['checkout/total'];
    }

    shippingMethods() {
        return this.$store.state.shippingMethods.shipping_methods
    }

    setShippingMethods(methods) {
        return this.$store.commit('shippingMethods/setShippingMethods', methods)
    }

    changeSelectedShippingMethod(method) {
        return this.$store.dispatch('shippingMethods/setSelectedShippingMethod', method);
    }

    getDefaults() {
        return {
            total: {
                label: this.$store.state.checkout.config.title,
                amount: this.$store.getters['checkout/total']
            },
            requiredBillingContactFields: [
                'email',
                'name',
                'phone',
                'postalAddress',
            ],
            requiredShippingContactFields: [
                'email',
                'name',
                'phone',
                'postalAddress',
            ],
            countryCode: 'US',
            currencyCode: 'USD',
            supportedNetworks: ['visa', 'masterCard', 'amex', 'discover'],
            merchantCapabilities: ['supports3DS', 'supportsCredit', 'supportsDebit'],
        };
    }

    async onPaymentMethodSelected() {
        try {
            await this.instance.fetchCheckout();
            const newTotal = this._getLine('Total', this.total());
            this.instance.applePaySession.completePaymentMethodSelection(newTotal, this._normalizeNewLineItems());
        } catch (error) {
            console.error("onPaymentMethodSelected error:", error)
        }
    }

    async onShippingContactSelected(event) {
        const {locality, countryCode, administrativeArea, postalCode} = event.shippingContact;

        const params = {
            city: locality || '',
            country: countryCode || 'US',
            state: administrativeArea || '',
            zip_code: postalCode || '',
        };

        try {
            const {data: {data}} = await CheckoutApi.getShipping(params);

            if (!data.length) {
                this.instance.applePaySession.completeShippingContactSelection(ApplePaySession.STATUS_INVALID_SHIPPING_POSTAL_ADDRESS, [], this._getLine('Total', this.total()), []);
                return;
            }

            this.setShippingMethods(data);
            const shippingMethod = _.head(this.shippingMethods());
            await this.changeSelectedShippingMethod(shippingMethod)
            const checkout = await CheckoutApi.calculateTaxes(params)
            await this.$store.dispatch('updateCheckout', checkout.data);

            this.instance.applePaySession.completeShippingContactSelection(
                ApplePaySession.STATUS_SUCCESS,
                this._normalizeShippingMethods(),
                this._getLine('Total', this.total()),
                this._normalizeNewLineItems()
            );
        } catch (e) {
            console.error("onShippingContactSelected error ", e);
            this.instance.applePaySession.completeShippingContactSelection(ApplePaySession.STATUS_INVALID_SHIPPING_POSTAL_ADDRESS, [], this._getLine('Total', this.total()), []);
        }
    }

    async onShippingMethodSelected(event) {
        const shipping_method = event.shippingMethod;
        const identifier = shipping_method.identifier.split('|', 2);

        let shippingMethod = {
            label: shipping_method.label,
            price: shipping_method.amount,
            service_code: identifier[0],
            package_code: identifier[1],
        };

        try {
            await this.changeSelectedShippingMethod(shippingMethod)

            this.instance.applePaySession.completeShippingMethodSelection({
                status: ApplePaySession.STATUS_SUCCESS,
                newLineItems: this._normalizeNewLineItems(),
                newTotal: this._getLine('Total', this.total()),
            });
        } catch (e) {
            console.error('onShippingMethodSelected error', e);
            this.instance.applePaySession.completeShippingMethodSelection({
                status: ApplePaySession.STATUS_FAILURE,
                newLineItems: this._normalizeNewLineItems(),
                newTotal: this._getLine('Total', this.total()),
            });
        }
    }

    onCancel() {
        this.setRequestStatus(false);
        this.instance.$notify.info("Your payment was cancelled")
    }

    onPaymentAuthorized(event, payload) {
        this.setRequestStatus(true);
        CheckoutApi.submitPayment(this.normalizeCheckoutRequest(event, payload))
            .then((response) => {
                const {status, message} = CheckoutApi.handlePaymentResponse(response)

                if (status !== 'success') {
                    this.instance.$notify({
                        type: status || 'error',
                        message: message,
                        duration: 10000
                    });
                    this.instance.applePaySession.completePayment(ApplePaySession.STATUS_FAILURE);
                    this.setRequestStatus(false);
                } else {
                    this.instance.applePaySession.completePayment(ApplePaySession.STATUS_SUCCESS);
                }
            }).catch((error) => {
            console.error("Error send payment to the API", error);
            this.instance.$handleError(error, 'Could not place your order. Please try again later.');
            this.instance.applePaySession.completePayment(ApplePaySession.STATUS_FAILURE);
            this.setRequestStatus(false);
        }).catch((error) => {
            this.instance.$handleError(error, 'Could not place your order. Please try again later.');
            this.instance.applePaySession.completePayment(ApplePaySession.STATUS_FAILURE);
            this.setRequestStatus(false);
        });
    }

    normalizeCheckoutRequest(event, payload) {
        const contact = this.getContact(event);

        return {
            contact,
            shipping: this.getShipping(event),
            billing: {
                ...this.getBilling(event),
                phone: contact.phone,
                email: contact.email,
            },
            payment_data: Object.assign({}, payload, {deviceData: this.instance.deviceData}),
            payment_type: this.instance.paymentMethodName,
        };
    }

    getContact(event) {
        const {shippingContact} = event.payment;

        return {
            email: shippingContact.emailAddress,
            phone: shippingContact.phoneNumber,
        }
    }

    getShipping(event) {
        const {shippingContact} = event.payment;
        const data = this.$store.state.shipping;

        return {
            address_line_1: _.head(shippingContact.addressLines),
            address_line_2: shippingContact.addressLines.length > 1 ? _.last(shippingContact.addressLines) : '',
            city: shippingContact.locality,
            company: data.company,
            country: shippingContact.countryCode || 'US',
            email: shippingContact.emailAddress,
            first_name: shippingContact.givenName,
            last_name: shippingContact.familyName,
            package_code: data.package_code,
            service_code: data.service_code,
            shipping_email: shippingContact.emailAddress,
            shipping_method_preview: data.shipping_method_preview,
            state: shippingContact.administrativeArea,
            zip_code: shippingContact.postalCode,
        };
    }

    getBilling(event) {
        const {billingContact, shippingContact} = event.payment;

        return {
            address_line_1: _.head(billingContact.addressLines),
            address_line_2: billingContact.addressLines.length > 1 ? _.last(billingContact.addressLines) : '',
            city: billingContact.locality,
            company: '',
            country: billingContact.countryCode || 'US',
            first_name: billingContact.givenName || shippingContact.givenName,
            last_name: billingContact.familyName || shippingContact.familyName,
            state: billingContact.administrativeArea,
            zip_code: billingContact.postalCode,
            isSameAsShipping: false,
        };
    }

    _getLine(label, amount, type) {
        return {
            type: type || 'final',
            label: label,
            amount: amount,
        }
    }

    _normalizeShippingMethods() {
        return this.shippingMethods().map(method => {
            return {
                label: method.label,
                amount: method.price,
                detail: '',
                identifier: method.service_code + '|' + method.package_code,
            }
        });
    }

    _normalizeNewLineItems() {
        const subTotal = parseFloat(this.checkout().subtotal).toFixed(2);
        const taxTotal = parseFloat(this.checkout().tax_total).toFixed(2);
        const shippingAmount = parseFloat(this.checkout().shipping).toFixed(2);

        let newLineItems = [];
        newLineItems.push(this._getLine('Subtotal', subTotal))
        newLineItems.push(this._getLine('Tax', taxTotal));
        newLineItems.push(this._getLine('Shipping', shippingAmount))

        return newLineItems;
    }
}

export default new ApplePayApi();
